import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Grid, 
    Paper, 
    Typography, 
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Button,
    Divider
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { RestaurantService } from '../../../api/services/restaurantService';
import ExportFormatModal from '../../../components/ExportFormatModal';
import { exportClientSide } from '../../../utils/exportUtils';
import { toast } from '../../../utils/toast';
import ReplayIcon from '@mui/icons-material/Replay';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { formatCurrency } from '../../../utils/currency';
import { standardFilterInputSx, standardResetButtonSx, standardSecondaryButtonSx } from '../../../components/common/tableStyles';

export default function RevenueReportTab() {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [exportModalOpen, setExportModalOpen] = useState(false);

    useEffect(() => {
        fetchRevenueReport();
    }, [dateRange]);

    const fetchRevenueReport = async () => {
        setLoading(true);
        try {
            const params = {};
            if (dateRange.start) params.startDate = dateRange.start;
            if (dateRange.end) params.endDate = dateRange.end;
            const data = await RestaurantService.getRestaurantRevenueReport(params);
            setReport(data || {});
        } catch (error) {
            console.error('Failed to fetch restaurant revenue report:', error);
            // Handled via global interceptor
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num) => {
        return new Intl.NumberFormat('en-US').format(num || 0);
    };

    const handleExport = (format) => {
        setExportModalOpen(false);
        if (!breakdownList || !breakdownList.length) {
            toast.error('No data available to export');
            return;
        }
        try {
            exportClientSide(format, breakdownList, 'revenue_report');
            toast.success('Export successful');
        } catch (error) {
            console.error('Export failed:', error);
            toast.error(error.message || 'Export failed');
        }
    };

    const renderMetricCard = (title, value, icon, loadingState, color = 'primary.main', isCurrency = false, prevValue = null) => (
        <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #eee', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }} elevation={0}>
            <Box>
                <Typography variant="body2" color="text.secondary" fontWeight="bold" sx={{ mb: 1 }}>{title}</Typography>
                {loadingState ? (
                    <Skeleton variant="text" width={120} height={40} />
                ) : (
                    <>
                        <Typography variant="h4" fontWeight="bold">
                            {isCurrency ? formatCurrency(value) : formatNumber(value)}
                        </Typography>
                        {prevValue !== null && prevValue !== undefined && (
                            <Typography variant="caption" color={value >= prevValue ? 'success.main' : 'error.main'}>
                                {value >= prevValue ? '▲' : '▼'} {isCurrency ? formatCurrency(Math.abs(value - prevValue)) : formatNumber(Math.abs(value - prevValue))} vs prev
                            </Typography>
                        )}
                    </>
                )}
            </Box>
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${color}15`, color: color }}>
                {icon}
            </Box>
        </Paper>
    );

    // Normalize breakdown data whether it's an object { Food: 100 } or an array [{ category: 'Food', amount: 100 }]
    const normalizeBreakdown = (breakdownData) => {
        if (!breakdownData) return [];
        if (Array.isArray(breakdownData)) {
            // Check if it's the topItems format
            if (breakdownData.length > 0 && breakdownData[0].menuItem) {
                return breakdownData.map(item => ({
                    label: item.menuItem?.name || 'Unknown Item',
                    subLabel: item.menuItem?.category?.name || 'Uncategorized',
                    quantity: item.totalQuantity || 0,
                    amount: Number(item.totalRevenue || item.amount || 0)
                }));
            }
            return breakdownData.map(item => ({
                label: item.category || item.name || item.label || 'Unknown',
                amount: Number(item.amount || item.total || item.revenue || 0)
            }));
        }
        if (typeof breakdownData === 'object') {
            return Object.keys(breakdownData).map(key => ({
                label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
                amount: Number(breakdownData[key])
            }));
        }
        return [];
    };

    const breakdownList = normalizeBreakdown(report?.topItems || report?.breakdown || report?.categories);

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AnalyticsIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                    <Typography variant="h5" fontWeight="bold">
                        POS Revenue & Analytics Report
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField 
                        size="small" 
                        type="date" 
                        value={dateRange.start} 
                        onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })} 
                        sx={standardFilterInputSx}
                    />
                    <TextField 
                        size="small" 
                        type="date" 
                        value={dateRange.end} 
                        onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })} 
                        sx={standardFilterInputSx}
                    />
                    {(dateRange.start || dateRange.end) && (
                        <Button variant="outlined" startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} sx={standardResetButtonSx} onClick={() => setDateRange({ start: '', end: '' })}>Reset</Button>
                    )}
                    <Button variant="outlined" startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />} sx={standardSecondaryButtonSx} onClick={() => setExportModalOpen(true)}>Export Options</Button>
                    <ExportFormatModal
                        open={exportModalOpen}
                        onClose={() => setExportModalOpen(false)}
                        onExport={handleExport}
                        isExporting={false}
                        title="Export Revenue Report"
                    />
                </Box>
            </Box>

            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                    {renderMetricCard(
                        "Total Gross Revenue", 
                        report?.totalRevenue || report?.grossRevenue || report?.totalAmount || 0, 
                        <AttachMoneyIcon />, 
                        loading, 
                        '#2e7d32', 
                        true,
                        report?.prevTotalRevenue || report?.prevGrossRevenue || null
                    )}
                </Grid>
                <Grid item xs={12} md={4}>
                    {renderMetricCard(
                        "Average Order Value (AOV)", 
                        report?.averageOrderValue || report?.aov || 0, 
                        <AnalyticsIcon />, 
                        loading, 
                        '#0288d1', 
                        true,
                        report?.prevAverageOrderValue || report?.prevAov || null
                    )}
                </Grid>
                <Grid item xs={12} md={4}>
                    {renderMetricCard(
                        "Total Tickets Processed", 
                        report?.totalOrders || report?.totalTickets || report?.ticketCount || 0, 
                        <ReceiptIcon />, 
                        loading, 
                        '#ed6c02', 
                        false,
                        report?.prevTotalOrders || report?.prevTotalTickets || null
                    )}
                </Grid>
            </Grid>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #eaecf0' }} elevation={0}>
                <Box sx={{ p: 3, borderBottom: '1px solid #eaecf0', bgcolor: '#f8fafc' }}>
                    <Typography variant="h6" fontWeight="bold">Revenue Breakdown Summary</Typography>
                    <Typography variant="body2" color="text.secondary">Aggregated revenue segmented by operational category streams</Typography>
                </Box>
                <TableContainer sx={{ border: 'none', borderRadius: 0, boxShadow: 'none' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Item / Category</TableCell>
                                {breakdownList[0]?.quantity !== undefined && (
                                    <TableCell align="center">Qty Sold</TableCell>
                                )}
                                <TableCell align="right">Revenue Generated</TableCell>
                                <TableCell align="right">% of Gross</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                Array.from(new Array(3)).map((_, i) => (
                                    <TableRow key={`skeleton-${i}`}>
                                        <TableCell><Skeleton width="60%" /></TableCell>
                                        {breakdownList[0]?.quantity !== undefined && <TableCell align="center"><Skeleton width="20%" sx={{ mx: 'auto' }} /></TableCell>}
                                        <TableCell align="right"><Skeleton width="40%" sx={{ ml: 'auto' }} /></TableCell>
                                        <TableCell align="right"><Skeleton width="30%" sx={{ ml: 'auto' }} /></TableCell>
                                    </TableRow>
                                ))
                            ) : breakdownList.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary">No category breakdown data available for this report cycle.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                breakdownList.map((item, idx) => {
                                    const total = report?.totalRevenue || report?.grossRevenue || report?.totalAmount || 1;
                                    const percentage = total > 0 ? ((item.amount / total) * 100).toFixed(1) : 0;
                                    return (
                                        <TableRow key={idx} hover>
                                            <TableCell>
                                                <Typography fontWeight="bold">{item.label}</Typography>
                                                {item.subLabel && <Typography variant="caption" color="text.secondary">{item.subLabel}</Typography>}
                                            </TableCell>
                                            {item.quantity !== undefined && (
                                                <TableCell align="center">
                                                    <Typography variant="body2">{item.quantity}</Typography>
                                                </TableCell>
                                            )}
                                            <TableCell align="right">
                                                <Typography color="primary.main" fontWeight="bold">
                                                    {formatCurrency(item.amount)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography color="text.secondary" variant="body2">
                                                    {percentage}%
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
}
